import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Check, X, Loader2, RotateCcw, Stethoscope } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Row {
  id: string;
  session_id: string;
  requested_by: string;
  reason: string;
  created_at: string;
  attendance_sessions?: { topic: string | null; module_code: string | null; class: string | null; session_date: string | null } | null;
}

/**
 * Approval queue for re-scan and remedial-session requests.
 * Visible to Principal / Deputy Principal / Superadmin. Renders nothing
 * for other roles or when both queues are empty.
 */
export function SessionRequestApprovals() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const canApprove = hasRole("superadmin") || hasRole("principal") || hasRole("deputy_principal");
  const [reopen, setReopen] = useState<Row[]>([]);
  const [remedial, setRemedial] = useState<Row[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    if (!canApprove) { setLoading(false); return; }
    setLoading(true);
    const reopenSel = "id, session_id, requested_by, reason, created_at, attendance_sessions!session_reopen_requests_session_id_fkey(topic, module_code, class, session_date)";
    const remedialSel = "id, session_id, requested_by, reason, created_at, attendance_sessions!remedial_session_requests_session_id_fkey(topic, module_code, class, session_date)";
    const [a, b] = await Promise.all([
      (supabase as any).from("session_reopen_requests").select(reopenSel).eq("status", "pending").order("created_at", { ascending: true }),
      (supabase as any).from("remedial_session_requests").select(remedialSel).eq("status", "pending").order("created_at", { ascending: true }),
    ]);
    const err = a.error?.message || b.error?.message || null;
    setLoadError(err);
    setReopen((a.data ?? []) as Row[]);
    setRemedial((b.data ?? []) as Row[]);
    setLoading(false);
  }, [canApprove]);

  useEffect(() => { load(); }, [load]);

  async function decide(kind: "reopen" | "remedial", row: Row, action: "approved" | "rejected") {
    setBusy(row.id);
    const fn = kind === "reopen" ? "decide_session_reopen_request" : "decide_remedial_session_request";
    const { error } = await (supabase as any).rpc(fn, {
      _request_id: row.id,
      _action: action,
      _comment: comment.trim() || null,
    });
    setBusy(null);
    setComment("");
    if (error) return toast({ title: "Failed", description: error.message, variant: "destructive" });
    toast({ title: action === "approved" ? "Approved" : "Rejected" });
    load();
  }

  if (!canApprove) return null;

  const renderList = (kind: "reopen" | "remedial", rows: Row[]) =>
    rows.map((r) => {
      const s = r.attendance_sessions;
      return (
        <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border rounded-md p-2.5 text-sm">
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{s?.topic || "Untitled"} <span className="text-muted-foreground">({s?.module_code || "—"} · {s?.class || "—"})</span></div>
            <div className="text-xs text-muted-foreground truncate">Reason: {r.reason}</div>
            <div className="text-[11px] text-muted-foreground">Submitted {format(new Date(r.created_at), "PPp")}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy === r.id} onClick={() => decide(kind, r, "approved")}>
              {busy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}Approve
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={busy === r.id}><X className="w-4 h-4 mr-1" />Reject</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reject this request?</AlertDialogTitle>
                  <AlertDialogDescription>Add a comment (optional) to inform the requester.</AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-1.5">
                  <Label htmlFor={`cmt-${r.id}`}>Comment</Label>
                  <Textarea id={`cmt-${r.id}`} value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setComment("")}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => decide(kind, r, "rejected")}>Reject</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      );
    });

  return (
    <Card className="border-accent/40 bg-accent/5">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          Pending session approvals
          <Badge variant="secondary">{reopen.length + remedial.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking re-scan and remedial requests…
          </div>
        )}
        {!loading && loadError && (
          <div className="text-sm text-destructive py-2">Could not load session requests: {loadError}</div>
        )}
        {!loading && !loadError && reopen.length === 0 && remedial.length === 0 && (
          <div className="text-sm text-muted-foreground py-2">No pending re-scan or remedial-session requests.</div>
        )}
        {reopen.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" />Re-scan requests</div>
            {renderList("reopen", reopen)}
          </div>
        )}
        {remedial.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs font-semibold flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5" />Remedial session requests</div>
            {renderList("remedial", remedial)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
